import { useState, useCallback } from 'react';
import { socket } from '../lib/socket';
import { useSocketEvent } from './useSocketEvent';
import { EVENTS } from '@flip7/shared';
import type {
  HotStreakState,
  HotStreakPrivateState,
  RaceCardResult,
  RaceFoldResult,
  BetSide,
} from '@flip7/shared';

export function useHotStreak(roomCode: string | null) {
  const [hsState, setHsState] = useState<HotStreakState | null>(null);
  const [privateState, setPrivateState] = useState<HotStreakPrivateState | null>(null);
  const [lastRaceCard, setLastRaceCard] = useState<RaceCardResult | null>(null);
  const [lastFold, setLastFold] = useState<RaceFoldResult | null>(null);

  useSocketEvent<HotStreakState>(EVENTS.HOT_STREAK_STATE, (data) => {
    setHsState(data);
  });

  useSocketEvent<HotStreakPrivateState>(EVENTS.HOT_STREAK_PRIVATE, (data) => {
    setPrivateState(data);
  });

  useSocketEvent<RaceCardResult>(EVENTS.HOT_STREAK_RACE_CARD, (data) => {
    setLastRaceCard(data);
  });

  useSocketEvent<RaceFoldResult>(EVENTS.HOT_STREAK_RACE_FOLD, (data) => {
    setLastFold(data);
  });

  const startGame = useCallback(() => {
    if (!roomCode) return;
    socket.emit(EVENTS.HOT_STREAK_START_GAME, { roomCode });
  }, [roomCode]);

  const draftPick = useCallback(
    (ticketId: string, side: BetSide) => {
      if (!roomCode) return;
      socket.emit(EVENTS.HOT_STREAK_DRAFT_PICK, { roomCode, ticketId, side });
    },
    [roomCode],
  );

  const submitCard = useCallback(
    (cardId: string) => {
      if (!roomCode) return;
      socket.emit(EVENTS.HOT_STREAK_SUBMIT_CARD, { roomCode, cardId });
    },
    [roomCode],
  );

  const doubleDown = useCallback(
    (betIndex: number) => {
      if (!roomCode) return;
      socket.emit(EVENTS.HOT_STREAK_DOUBLE_DOWN, { roomCode, betIndex });
    },
    [roomCode],
  );

  return {
    hsState,
    privateState,
    lastRaceCard,
    lastFold,
    startGame,
    draftPick,
    submitCard,
    doubleDown,
  };
}
